import { Component, type ErrorInfo, type ReactNode } from 'react';

import * as Sentry from '@sentry/react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    Sentry.captureException(error, { extra: { componentStack: info.componentStack } });
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private reset = () => {
    this.setState({ hasError: false, error: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;

      return (
        <div className='flex min-h-screen flex-col items-center justify-center gap-6 p-8 text-center'>
          <div className='flex h-16 w-16 items-center justify-center rounded-full bg-red-100'>
            <svg className='h-8 w-8 text-red-600' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
              />
            </svg>
          </div>
          <div className='space-y-2'>
            <h2 className='text-lg font-semibold text-gray-900'>Une erreur inattendue s&apos;est produite</h2>
            {this.state.error?.message && <p className='max-w-sm text-sm text-gray-500'>{this.state.error.message}</p>}
          </div>
          <button
            onClick={this.reset}
            className='rounded-md bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 focus:ring-offset-2'
          >
            Retour à l&apos;accueil
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
